export const formatDate = (
  date: Date | string,
  format:
    | "short"
    | "long"
    | "longWithTime"
    | "time"
    | "lastcomms"
    | "default"
    | "day"
    | "hour"
    | "month"
    | "year"
    | "ddMMMyyyy"
    | "MMMyyyy" = "default"
): string => {
  if (!date) return "";

  // If the date is a string, parse it into a Date object
  const dateObj = typeof date === "string" ? new Date(date) : date;

  const options: Intl.DateTimeFormatOptions = {};

  switch (format) {
    case "short":
      options.year = "numeric";
      options.month = "short";
      options.day = "numeric";
      break;
    case "long":
      options.weekday = "long";
      options.year = "numeric";
      options.month = "long";
      options.day = "numeric";
      break;
    case "longWithTime":
      options.weekday = "long";
      options.year = "numeric";
      options.month = "long";
      options.day = "numeric";
      options.hour = "2-digit";
      options.minute = "2-digit";
      break;
    case "time":
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.second = "2-digit";
      break;
    case "lastcomms":
      options.year = "numeric";
      options.month = "numeric";
      options.day = "numeric";
      options.hour = "2-digit";
      options.minute = "2-digit";
      break;
    case "hour":
      options.hour = "2-digit";
      // options.timeZone = "UTC";
      break;
    case "day":
      options.day = "numeric";
      // options.timeZone = "UTC";
      break;
    case "month":
      options.month = "short";
      break;
    case "year":
      options.year = "numeric";
      break;
    case "ddMMMyyyy":
      return `${dateObj.getDate()} ${dateObj.toLocaleString("en-EU", {
        month: "short",
      })} ${dateObj.getFullYear()}`;
    case "MMMyyyy":
      return `${dateObj.toLocaleString("en-EU", {
        month: "short",
      })} ${dateObj.getFullYear()}`;

    default:
      options.day = "2-digit";
      options.month = "2-digit";
      options.year = "numeric";
  }

  return new Intl.DateTimeFormat(undefined, options).format(dateObj);
};

export const formatDateTimezone = (
  date: Date | string,
  format:
    | "default"
    | "day"
    | "hour"
    | "month"
    | "year"
    | "ddMMMyyyy"
    | "MMMyyyy" = "default",
  timezone: number = 0
): string => {
  if (!date) return "";

  // Normalize to Date
  const d = typeof date === "string" ? new Date(date) : new Date(date);

  // Apply timezone offset (in hours)
  const utc = d.getTime() + d.getTimezoneOffset() * 60000;
  const localDate = new Date(utc + timezone * 3600000);

  switch (format) {
    case "day":
      return String(localDate.getDate());
    case "hour":
      return String(localDate.getHours());
    case "month":
      return localDate.toLocaleDateString(undefined, { month: "short" });
    case "year":
      return String(localDate.getFullYear());
    case "ddMMMyyyy":
      return localDate.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    case "MMMyyyy":
      return localDate.toLocaleDateString(undefined, {
        month: "short",
        year: "numeric",
      });
    case "default":
    default:
      return localDate.toISOString();
  }
};

export const formatDateToZ = (
  date: Date,
  endOfDay = false,
  granularity: "day" | "month" | "year" = "day",
  timezone: number | undefined = undefined
) => {
  // Use local date parts consistently to avoid timezone confusion
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  let newDate: Date;

  switch (granularity) {
    case "day":
      newDate = new Date(Date.UTC(year, month, day));
      newDate.setUTCHours(
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0
      );
      break;

    case "month":
      if (endOfDay) {
        // Get the last day of the month
        newDate = new Date(Date.UTC(year, month + 1, 0));
        newDate.setUTCHours(23, 59, 59, 999);
      } else {
        // First day of the month
        newDate = new Date(Date.UTC(year, month, 1));
        newDate.setUTCHours(0, 0, 0, 0);
      }
      break;

    case "year":
      if (endOfDay) {
        // December 31st of the year
        newDate = new Date(Date.UTC(year, 11, 31));
        newDate.setUTCHours(23, 59, 59, 999);
      } else {
        // January 1st of the year
        newDate = new Date(Date.UTC(year, 0, 1));
        newDate.setUTCHours(0, 0, 0, 0);
      }
      break;

    default:
      throw new Error("Invalid granularity. Use 'day', 'month', or 'year'.");
  }

  // Apply optional custom timezone offset (in hours) and return ISO in UTC
  let shifted = newDate;
  if (timezone !== undefined) {
    shifted = new Date(newDate.getTime() - timezone * 3600000);
  }
  return shifted.toISOString();
};

export const getDayAndMonth = (date: Date | string): string => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  const day = dateObj.getDate();
  const month = dateObj.toLocaleString("en-EU", { month: "short" });

  return `${day} ${month}`;
};

/**
 * Converts a number of minutes to a time string in HH:mm format.
 * @param minutes Number of minutes
 * @returns Time string in HH:mm
 */
export function minutesToTimeString(minutes: number): string {
  if (typeof minutes !== "number" || isNaN(minutes)) return "00:00";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
}

export const expiredDate = (
  date: Date | string,
  days: number
): { expired: boolean; daysLeft: number } => {
  if (!date) return { expired: true, daysLeft: 0 };

  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();

  const expirationDate = new Date(
    dateObj.getTime() + days * 24 * 60 * 60 * 1000
  );

  // calculate remaining days (rounded down)
  const diffMs = expirationDate.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

  return {
    expired: now > expirationDate,
    daysLeft,
  };
};

/**
 * Converts local time to UTC by applying the timezone offset.
 * @param hour Local hour (0-23)
 * @param minute Local minute (0-59)
 * @returns Object with UTC hour and minute
 */
export const convertLocalTimeToUTC = (
  hour: number,
  minute: number = 0
): { hour: number; minute: number } => {
  // Get timezone offset in minutes (positive for west of UTC, negative for east)
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();
  const timezoneOffsetHours = Math.floor(timezoneOffsetMinutes / 60);

  // Calculate UTC hour
  let utcHour = hour + timezoneOffsetHours;

  // Handle day overflow/underflow
  if (utcHour < 0) {
    utcHour += 24;
  } else if (utcHour >= 24) {
    utcHour -= 24;
  }

  return {
    hour: utcHour,
    minute,
  };
};

/**
 * Converts UTC time to local time by applying the timezone offset.
 * @param hour UTC hour (0-23)
 * @param minute UTC minute (0-59)
 * @returns Object with local hour and minute
 */
export const convertUTCToLocalTime = (
  hour: number,
  minute: number = 0
): { hour: number; minute: number } => {
  // Get timezone offset in minutes (positive for west of UTC, negative for east)
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();
  const timezoneOffsetHours = Math.floor(timezoneOffsetMinutes / 60);

  // Calculate local hour (reverse of the UTC conversion)
  let localHour = hour - timezoneOffsetHours;

  // Handle day overflow/underflow
  if (localHour < 0) {
    localHour += 24;
  } else if (localHour >= 24) {
    localHour -= 24;
  }

  return {
    hour: localHour,
    minute,
  };
};
